import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';

const SchedulingGrid = ({
  scheduleData,
  viewMode,
  selectedDate,
  selectedProductionLine,
  onAssignmentDrop
}) => {
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverCell, setDragOverCell] = useState(null);
  const gridRef = useRef(null);

  const timeSlots = [
    '00:00', '02:00', '04:00', '06:00', '08:00', '10:00',
    '12:00', '14:00', '16:00', '18:00', '20:00', '22:00'
  ];

  const getAvailabilityColor = (availability) => {
    switch (availability) {
      case 'available':
        return 'var(--color-success)';
      case 'partial':
        return 'var(--color-warning)';
      case 'unavailable':
        return 'var(--color-error)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getAvailabilityBg = (availability) => {
    switch (availability) {
      case 'available':
        return 'bg-success/10';
      case 'partial':
        return 'bg-warning/10';
      case 'unavailable':
        return 'bg-error/10';
      default:
        return 'bg-muted';
    }
  };

  const handleDragStart = (e, worker, assignment) => {
    setDraggedItem({ worker, assignment });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, workerId, timeSlot) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCell({ workerId, timeSlot });
  };

  const handleDragLeave = () => {
    setDragOverCell(null);
  };

  const handleDrop = (e, workerId, timeSlot) => {
    e?.preventDefault();
    if (draggedItem) {
      onAssignmentDrop(workerId, timeSlot, draggedItem);
    }
    setDraggedItem(null);
    setDragOverCell(null);
  };

  const getAssignmentsForWorkerAndTime = (workerId, timeSlot) => {
    return scheduleData?.assignments?.filter(assignment => {
      if (assignment?.workerId !== workerId) return false;
      const assignmentStart = parseInt(assignment?.startTime?.split(':')?.[0]);
      const slotTime = parseInt(timeSlot?.split(':')?.[0]);
      const assignmentEnd = parseInt(assignment?.endTime?.split(':')?.[0]);
      return assignmentStart <= slotTime && slotTime < assignmentEnd;
    });
  };

  const isDropTarget = (workerId, timeSlot) => {
    return dragOverCell?.workerId === workerId && dragOverCell?.timeSlot === timeSlot;
  };

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      <div className="overflow-x-auto" ref={gridRef}>
        <div className="min-w-[1200px]">
          <div className="grid grid-cols-[200px_repeat(12,1fr)] border-b border-border">
            <div className="p-4 bg-muted font-semibold text-sm text-foreground sticky left-0 z-10">
              Worker / Time
            </div>
            {timeSlots?.map((slot) => (
              <div
                key={slot}
                className="p-2 text-center bg-muted font-medium text-xs text-muted-foreground border-l border-border"
              >
                {slot}
              </div>
            ))}
          </div>

          <div className="divide-y divide-border">
            {scheduleData?.workers?.map((worker) => (
              <div key={worker?.id} className="grid grid-cols-[200px_repeat(12,1fr)] hover:bg-muted/30 transition-colors duration-250">
                <div className="p-4 sticky left-0 z-10 bg-card border-r border-border">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-primary-foreground"
                      style={{ backgroundColor: getAvailabilityColor(worker?.availability) }}
                    >
                      {worker?.avatar}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{worker?.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{worker?.role}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: getAvailabilityColor(worker?.availability) }}
                        />
                        <span className="text-xs capitalize text-muted-foreground">
                          {worker?.availability}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {timeSlots?.map((slot) => {
                  const assignments = getAssignmentsForWorkerAndTime(worker?.id, slot);
                  const isTarget = isDropTarget(worker?.id, slot);
                  const isUnavailable = worker?.availability === 'unavailable';

                  return (
                    <div
                      key={slot}
                      className={`relative p-1 border-l border-border min-h-[80px] ${
                        isTarget ? 'bg-primary/20' : ''
                      } ${
                        isUnavailable ? 'bg-error/5 cursor-not-allowed' : 'cursor-pointer'
                      }`}
                      onDragOver={(e) => !isUnavailable && handleDragOver(e, worker?.id, slot)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => !isUnavailable && handleDrop(e, worker?.id, slot)}
                    >
                      {assignments?.map((assignment) => (
                        <div
                          key={assignment?.id}
                          className="mb-1 p-2 bg-primary/90 text-primary-foreground rounded text-xs cursor-move hover:bg-primary transition-colors duration-250"
                          draggable={!isUnavailable}
                          onDragStart={(e) => handleDragStart(e, worker, assignment)}
                        >
                          <div className="flex items-center justify-between gap-1">
                            <Icon name="GripVertical" size={12} />
                            <span className="flex-1 truncate font-medium">{assignment?.taskName}</span>
                          </div>
                          <div className="text-[10px] opacity-80 mt-1">
                            {assignment?.startTime} - {assignment?.endTime}
                          </div>
                        </div>
                      ))}
                      {isTarget && (
                        <div className="absolute inset-0 border-2 border-dashed border-primary rounded pointer-events-none" />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="p-4 bg-muted border-t border-border">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--color-success)' }} />
            <span className="text-xs text-muted-foreground">Available</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--color-warning)' }} />
            <span className="text-xs text-muted-foreground">Partial Availability</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--color-error)' }} />
            <span className="text-xs text-muted-foreground">Unavailable</span>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Icon name="Info" size={14} color="var(--color-muted-foreground)" />
            <span className="text-xs text-muted-foreground">Drag tasks to assign workers to time slots</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchedulingGrid;